import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Checkbox,
  Divider,
  CircularProgress,
} from "@mui/material";
import QRCode from "react-qr-code";
import { Icon } from "@iconify/react";
import downloadFill from "@iconify/icons-eva/download-fill";
import chevronDownFill from "@iconify/icons-eva/chevron-down-fill";
import { useQuery } from "@tanstack/react-query";
import { fetchApi } from "src/react-query/fetchWithError";
import { connectorUpiUrl } from "src/react-query/endPoints";
import { useGetDetail } from "src/hooks/useFetch.hook";
import enums from "src/util/enums";
import { toSafeId } from "src/utils/downloadQr";
import {
  getQrDataUrlFromContainer,
  downloadUpiInstructionBoard,
  BOARD_LANGUAGES,
} from "src/utils/upiInstructionBoard";

// Pick the price detail active right now for a variable price group (UTC).
const getActiveVariablePrice = (priceDetails) => {
  if (!Array.isArray(priceDetails)) return null;
  const todayUtc = new Date().getUTCDay();
  const nowUtc = new Date().toISOString().slice(11, 19); // HH:mm:ss
  return (
    priceDetails.find(
      (p) => p.day === todayUtc && p.startTime <= nowUtc && p.endTime >= nowUtc
    ) || null
  );
};

// Derive the customer-facing tariff for a connector's price group, mirroring
// PriceGroupPriceDisplay in ChargerForm. Returns { value, unit } or null.
const computeTariff = (priceGroupDetail) => {
  if (!priceGroupDetail) return null;
  const priceDetails =
    priceGroupDetail.priceDetails || priceGroupDetail.price_details;
  if (!Array.isArray(priceDetails) || priceDetails.length === 0) return null;

  const detail =
    priceGroupDetail.priceGroupType === "variable"
      ? getActiveVariablePrice(priceDetails)
      : priceDetails[0];
  if (!detail) return null;

  const priceType = detail.priceType || detail.price_type;
  const perKW = detail.priceperKW || detail.priceper_kw || detail.price_per_kw;
  const perHr = detail.priceperHr || detail.priceper_hr || detail.price_per_hr;
  const perMin =
    detail.priceperMin || detail.priceper_min || detail.price_per_min;

  if (priceType === enums.PRICE_PER.KWH && perKW && parseFloat(perKW) > 0) {
    return { value: `${parseFloat(perKW)}`, unitKey: "perKwh" };
  }
  if (priceType === enums.PRICE_PER.MIN || priceType === enums.PRICE_PER.HR) {
    if (perHr && parseFloat(perHr) > 0) {
      return { value: `${parseFloat(perHr)}`, unitKey: "perHour" };
    }
    if (perMin && parseFloat(perMin) > 0) {
      return { value: `${parseFloat(perMin)}`, unitKey: "perMin" };
    }
  }
  return null;
};

// Shared hook: fetch the Paytm UPI URL for a connector name (its name maps to a
// payment gateway key_id). Both variants use the same query key, so React Query
// dedupes them into a single request.
const useConnectorUpi = (teamId, chargerId, connectorName) => {
  const trimmedName = (connectorName ?? "").toString().trim();
  const enabled =
    trimmedName.length > 6 && Boolean(teamId) && Boolean(chargerId);

  const { data } = useQuery({
    queryKey: ["connectorUpi", teamId, chargerId, trimmedName],
    queryFn: () =>
      fetchApi({
        url: connectorUpiUrl(teamId, chargerId, trimmedName),
        method: "GET",
      }),
    enabled,
    staleTime: 60000,
  });

  return { trimmedName, enabled, upiUrl: data?.data?.upi_url || "" };
};

// A single "Download ▾" button whose dropdown menu offers the Paytm UPI
// instruction board (with / without the connector price) and the Razorpay UPI QR
// (download the existing QR, or generate one if none exists yet).
const ConnectorUpiDownload = ({
  hasPaytm,
  boardEnabled,
  qrDomId,
  upiUrl,
  chargerName,
  chargerId,
  connectorNumber,
  showConnectorId,
  tariff,
  tariffUnitKey,
  razorpayQrCodeUrl,
  onRazorpayDownload,
  onRazorpayGenerate,
  razorpayGenerating,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [downloading, setDownloading] = useState(null); // "with" | "without" | null
  const [languages, setLanguages] = useState(["en"]);
  const open = Boolean(anchorEl);
  const busy = Boolean(downloading) || Boolean(razorpayGenerating);

  const toggleLanguage = (code) => {
    setLanguages((prev) =>
      prev.includes(code)
        ? prev.filter((c) => c !== code)
        : [...prev, code]
    );
  };

  // The Paytm instruction-board download is Amik-domain only; Razorpay QR stays
  // available on every domain.
  const showBoard = hasPaytm && boardEnabled;

  const hasRazorpayQr =
    razorpayQrCodeUrl !== undefined &&
    razorpayQrCodeUrl !== null &&
    `${razorpayQrCodeUrl}`.trim() !== "";
  const canRazorpay = Boolean(onRazorpayDownload) || Boolean(onRazorpayGenerate);

  const closeMenu = () => setAnchorEl(null);

  const handleDownload = async (showPrice) => {
    closeMenu();
    if (downloading) return;
    setDownloading(showPrice ? "with" : "without");
    try {
      const qrDataUrl = await getQrDataUrlFromContainer(qrDomId);
      await downloadUpiInstructionBoard(
        {
          qrDataUrl,
          chargerName,
          chargerId,
          connectorId: connectorNumber,
          showConnectorId,
          tariff,
          tariffUnitKey,
        },
        { showPrice, languages: languages.length ? languages : ["en"] }
      );
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(
        e?.message || "Could not generate the UPI instruction board."
      );
    } finally {
      setDownloading(null);
    }
  };

  const handleRazorpay = () => {
    closeMenu();
    if (hasRazorpayQr) {
      onRazorpayDownload?.();
    } else {
      onRazorpayGenerate?.();
    }
  };

  const buttonLabel = downloading
    ? "Preparing…"
    : razorpayGenerating
    ? "Generating…"
    : "Download";

  return (
    <>
      <Button
        type="button"
        size="small"
        variant="contained"
        disabled={busy}
        startIcon={
          busy ? (
            <CircularProgress size={14} sx={{ color: "white" }} />
          ) : (
            <Icon icon={downloadFill} width={14} height={14} />
          )
        }
        endIcon={<Icon icon={chevronDownFill} width={16} height={16} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          background: "linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)",
          border: "2px solid rgba(255,255,255,0.3)",
          color: "white",
          fontWeight: 700,
          fontSize: "0.7rem",
          px: 2,
          py: 0.75,
          borderRadius: 2,
          textTransform: "none",
          height: 36,
          minWidth: 130,
          boxShadow: "0 4px 15px rgba(21, 101, 192, 0.4)",
          "&:hover": {
            background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
            border: "2px solid rgba(255,255,255,0.5)",
            boxShadow: "0 6px 20px rgba(21, 101, 192, 0.6)",
            transform: "translateY(-1px)",
          },
        }}
      >
        {buttonLabel}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 240 } }}
      >
        {showBoard && (
          <ListSubheader
            disableSticky
            sx={{ lineHeight: "32px", fontSize: "0.7rem", fontWeight: 700 }}
          >
            Languages
          </ListSubheader>
        )}
        {showBoard &&
          BOARD_LANGUAGES.map((lang) => (
            <MenuItem
              key={lang.code}
              dense
              onClick={() => toggleLanguage(lang.code)}
            >
              <Checkbox
                edge="start"
                size="small"
                checked={languages.includes(lang.code)}
                tabIndex={-1}
                disableRipple
                sx={{ py: 0, mr: 1 }}
              />
              <ListItemText primary={lang.native} />
            </MenuItem>
          ))}
        {showBoard && <Divider />}
        {showBoard && (
          <MenuItem
            onClick={() => handleDownload(true)}
            disabled={!languages.length}
          >
            <ListItemIcon>
              <Icon icon={downloadFill} width={18} height={18} />
            </ListItemIcon>
            <ListItemText
              primary="Paytm UPI board (with price)"
              secondary="Instruction board incl. tariff"
            />
          </MenuItem>
        )}
        {showBoard && (
          <MenuItem
            onClick={() => handleDownload(false)}
            disabled={!languages.length}
          >
            <ListItemIcon>
              <Icon icon={downloadFill} width={18} height={18} />
            </ListItemIcon>
            <ListItemText
              primary="Paytm UPI board (no price)"
              secondary="Instruction board without tariff"
            />
          </MenuItem>
        )}
        {showBoard && canRazorpay && <Divider />}
        {canRazorpay && (
          <MenuItem onClick={handleRazorpay}>
            <ListItemIcon>
              <Icon
                icon={hasRazorpayQr ? downloadFill : "eva:plus-circle-fill"}
                width={18}
                height={18}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                hasRazorpayQr ? "Razorpay UPI QR" : "Generate Razorpay UPI QR"
              }
            />
          </MenuItem>
        )}
      </Menu>

      {/* Hidden QR used only as the source for the downloaded Paytm board image. */}
      {showBoard && (
        <Box
          id={qrDomId}
          sx={{ position: "absolute", left: -9999, top: -9999 }}
          aria-hidden
        >
          <QRCode value={upiUrl} size={256} />
        </Box>
      )}
    </>
  );
};

/**
 * Static Paytm UPI helpers for a connector (its name maps to a payment gateway
 * key_id), plus the Razorpay UPI QR download, surfaced from one dropdown button.
 *
 * variant="download" -> a single "Download ▾" button whose menu offers the Paytm
 *                       UPI instruction board (with / without price) and the
 *                       Razorpay UPI QR (download existing / generate). Always
 *                       renders in edit mode; Paytm items appear only when a UPI
 *                       URL is configured, Razorpay items when handlers are passed.
 * variant="url"      -> the UPI URL shown as text (below the connector name);
 *                       renders only when a UPI URL is configured.
 */
export const ConnectorUpi = ({
  teamId,
  chargerId,
  connectorName,
  chargerName,
  connectorNumber,
  totalConnectors,
  tariff,
  priceGroupId,
  boardEnabled = false,
  variant = "url",
  razorpayQrCodeUrl,
  onRazorpayDownload,
  onRazorpayGenerate,
  razorpayGenerating,
}) => {
  const { trimmedName, enabled, upiUrl } = useConnectorUpi(
    teamId,
    chargerId,
    connectorName
  );

  // Resolve the connector tariff from its price group (same source as the form's
  // PriceGroupPriceDisplay); fall back to any flat tariff value passed in.
  const { data: priceGroupDetail } = useGetDetail("priceGroup", {
    id: priceGroupId,
  });
  const computedTariff = computeTariff(priceGroupDetail);
  const tariffValue = computedTariff?.value || tariff || "";
  const tariffUnitKey = computedTariff?.unitKey || "perKwh";

  const hasPaytm = enabled && Boolean(upiUrl);
  const qrDomId = `connector-upi-qr-${toSafeId(trimmedName || "connector")}`;

  if (variant === "download") {
    return (
      <ConnectorUpiDownload
        hasPaytm={hasPaytm}
        boardEnabled={boardEnabled}
        qrDomId={qrDomId}
        upiUrl={upiUrl}
        chargerName={chargerName}
        chargerId={chargerId}
        connectorNumber={connectorNumber}
        showConnectorId={Number(totalConnectors) > 1}
        tariff={tariffValue}
        tariffUnitKey={tariffUnitKey}
        razorpayQrCodeUrl={razorpayQrCodeUrl}
        onRazorpayDownload={onRazorpayDownload}
        onRazorpayGenerate={onRazorpayGenerate}
        razorpayGenerating={razorpayGenerating}
      />
    );
  }

  // variant === "url"
  if (!hasPaytm) return null;

  return (
    <Box sx={{ mt: 0.75 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontSize: "0.65rem",
          display: "block",
        }}
      >
        Paytm UPI
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontSize: "0.72rem", wordBreak: "break-all" }}
      >
        {upiUrl}
      </Typography>
    </Box>
  );
};

export default ConnectorUpi;
